import { useEffect, useState } from "react";

/**
 * A deployed build is a new set of hashed files (`/assets/index-<hash>.js`)
 * behind a `/index.html` that is never cached (deploy/nginx/autorepair.conf).
 * A tab that stays open keeps running the OLD bundle, and once the old
 * hashed files are gone its next lazy import can fail. Two remedies:
 *   1. checkForNewBuild() re-fetches index.html and compares the entry
 *      script name against the one this tab loaded -> "new version" banner.
 *   2. reloadOnStaleChunk() reloads once when Vite reports a chunk that no
 *      longer exists (`vite:preloadError`), instead of a broken screen.
 * Both are no-ops under the Vite dev server (no hashed entry; HMR handles it).
 */
const ENTRY_SCRIPT = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+\.js)"/;
const CHECK_INTERVAL_MS = 10 * 60 * 1000;
const RELOAD_GUARD_KEY = "ar-stale-chunk-reload";

function loadedEntry(): string | null {
  return document.querySelector('script[type="module"][src*="/assets/"]')?.getAttribute("src") ?? null;
}

async function checkForNewBuild(current: string): Promise<boolean> {
  try {
    const res = await fetch(import.meta.env.BASE_URL, { cache: "no-store", credentials: "same-origin" });
    if (!res.ok) return false;
    const latest = ENTRY_SCRIPT.exec(await res.text())?.[1];
    return latest !== undefined && latest !== current;
  } catch {
    return false; // offline or a blip — try again next time
  }
}

export function reloadOnStaleChunk(): void {
  window.addEventListener("vite:preloadError", (event) => {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      if (Date.now() - last < 60_000) return; // just reloaded and it still failed — don't loop
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch {
      return; // storage blocked: can't guard against a loop, so leave the error alone
    }
    event.preventDefault();
    window.location.reload();
  });
}

/** Shows a small "new version" bar once a newer build is deployed. The user
 * chooses when to reload, so nothing half-typed is thrown away. */
export function AppUpdateBanner() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const current = loadedEntry();
    if (!current) return;
    let cancelled = false;
    const check = (): void => {
      void checkForNewBuild(current).then((isNew) => {
        if (isNew && !cancelled) setStale(true);
      });
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, []);

  if (!stale) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-0 bottom-20 z-[70] mx-auto flex w-fit max-w-[calc(100vw-2rem)] items-center gap-3 rounded-xl bg-[#071a33] px-4 py-3 text-[13.5px] text-white md:bottom-6"
      style={{ boxShadow: "0 10px 30px rgba(0,0,0,.35)" }}
    >
      <span>A new version of Bay2Bill is available.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="flex-none rounded-lg bg-white px-3 py-1.5 text-[13px] font-bold text-[#071a33] hover:bg-[#e6f1f5]"
      >
        Refresh
      </button>
    </div>
  );
}
